import type { ProductGroupsData } from '@/entities/optimization-group'
import type {
	FilterRuleModel,
	OptimizationGroupModel,
	OptimizationModel,
	ScenarioModel,
} from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { JSONSchemaType } from 'ajv'
import type { ResolverResult, UseFormReturn } from 'react-hook-form'
import type { OptimizationGroupFormData } from '../model/types'

import React, {
	createContext,
	use,
	useCallback,
	useEffect,
	useMemo,
} from 'react'
import { toNestErrors } from '@hookform/resolvers'
import { useQueryClient } from '@tanstack/react-query'
import { fullFormats } from 'ajv-formats/dist/formats'
import { any, isEmpty, merge, prop } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useBeforeGroupChange,
	useCheckOptimizationForDeleteExport,
	useOptimization,
} from '@/entities/optimization'
import {
	getSalesEntitiesTree,
	useOptimizationGroupQuery,
	useProductGroupCatalogQuery,
	useSalesEntitiesQuery,
	useUpdateOptimizationGroupMutation,
} from '@/entities/optimization-group'
import {
	getSkuRules,
	useMainScenarioQuery,
	useUpdateMainScenarioMutation,
} from '@/entities/scenario'

import { OptimizationStatus } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import { waitGroupStatus } from '@/shared/lib/socket'
import { getCompactedSelection } from '@/shared/lib/tree'
import { ajvResolver } from '@/shared/lib/validator/ajv'
import { parseZod4Issues } from '@/shared/lib/validator/zod'

import { buildFormDefaultValue } from '../lib/buildFormDefaultValue'
import { useOptimizationGroupSchemaQuery } from '../lib/useOptimizationGroupSchema'
import { optimizationGroupFormSchema } from '../model/contract'

const groupsQueryParams = { queryKey: ['optimization-groups'] }
type OptimizationGroupItemContextType = {
	skuRules: FilterRuleModel[]
	optimizationGroup: OptimizationGroupModel
	optimization?: OptimizationModel
	mainScenario?: ScenarioModel
	methods: UseFormReturn<OptimizationGroupFormData>
	isLoading: boolean
	isPending: boolean
	salesEntitiesTree?: TreeNodeType[]
	productGroups?: ProductGroupsData
	handleSubmit: () => void
	handleSubmitAndUpdate: () => void
	handleReset: () => void
}
export const OptimizationGroupItemContext =
	createContext<OptimizationGroupItemContextType>({
		optimizationGroup: {} as OptimizationGroupModel,
		skuRules: [],
		methods: {} as UseFormReturn<OptimizationGroupFormData>,
		isLoading: false,
		isPending: false,
		handleSubmit: () => {},
		handleSubmitAndUpdate: () => {},
		handleReset: () => {},
	})
const allowedStatuses = [OptimizationStatus.New, OptimizationStatus.Pending]
const getScenarioPayload = (data: OptimizationGroupFormData) => ({
	settings: {
		strategy: data.strategy,
		business: data.business,
		stop_list: data.stop_list,
	},
})
export const OptimizationGroupItemProvider = ({
	optimizationGroupId,
	children,
}: {
	children: React.ReactNode
	optimizationGroupId: string
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { commit, isOptimizationLoading, optimization } =
		useOptimization(optimizationGroupId)
	const checkExport = useCheckOptimizationForDeleteExport(optimizationGroupId)
	const { onMutate } = useBeforeGroupChange(optimizationGroupId)
	const mainScenarioQuery = useMainScenarioQuery(optimizationGroupId)
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const productGroupsQuery = useProductGroupCatalogQuery()
	const schemaQuery = useOptimizationGroupSchemaQuery()
	const optimizationGroupQuery = useOptimizationGroupQuery(optimizationGroupId)
	const productGroups = productGroupsQuery.data
	const salesEntitiesTree = useMemo(
		() => getSalesEntitiesTree(salesEntitiesQuery.data),
		[salesEntitiesQuery.data]
	)
	const skuRules = useMemo(
		() =>
			mainScenarioQuery.data?.settings?.stop_list
				? getSkuRules(mainScenarioQuery.data.settings.stop_list)
				: [],
		[mainScenarioQuery.data?.settings.stop_list]
	)
	const methods = useForm<OptimizationGroupFormData>({
		resolver: getValidationResolver(schemaQuery.data!, { salesEntitiesTree }),
	})
	useEffect(() => {
		if (
			!isEmpty(salesEntitiesTree) &&
			productGroups !== undefined &&
			optimizationGroupQuery.data &&
			mainScenarioQuery.data
		) {
			methods.reset(
				buildFormDefaultValue({
					optimizationGroup: optimizationGroupQuery.data,
					pickerForest: productGroups.pickerForest,
					scenario: mainScenarioQuery.data!,
					salesEntitiesTree,
				})
			)
		}
	}, [
		optimizationGroupQuery.data,
		mainScenarioQuery.data,
		salesEntitiesTree,
		productGroups,
	])
	const updateMainScenarioMutation = useUpdateMainScenarioMutation(
		optimizationGroupId,
		{
			onMutate,
			onSuccess() {
				queryClient.invalidateQueries({ queryKey: ['optimization-groups'] })
				queryClient.invalidateQueries({ queryKey: ['main-scenario'] })
			},
		}
	)
	const updateOptimizationGroupMutation = useUpdateOptimizationGroupMutation(
		optimizationGroupId,
		{
			onSuccess(data) {
				queryClient.setQueryData(
					['optimization-groups', optimizationGroupId],
					data
				)
			},
		}
	)
	const isLoading =
		any(prop('isLoading'), [
			salesEntitiesQuery,
			productGroupsQuery,
			schemaQuery,
			optimizationGroupQuery,
		]) || isOptimizationLoading
	const isPending = any(prop('isPending'), [
		updateOptimizationGroupMutation,
		updateMainScenarioMutation,
	])
	const getOptimizationGroupPayload = (data: OptimizationGroupFormData) => {
		const { level, selected } = getCompactedSelection(
			salesEntitiesTree,
			data.sales_entities
		)

		return {
			sales_level_id: level,
			sales_entities: selected.map(({ id }) => id),
			product_groups: data.product_groups,
			name: data.name,
			description: data.description,
		}
	}
	const handleSubmit = methods.handleSubmit(
		checkExport(async (data: OptimizationGroupFormData) => {
			try {
				await updateMainScenarioMutation.mutateAsync(getScenarioPayload(data))
				if (optimization.status !== OptimizationStatus.Pending) {
					await waitGroupStatus(optimizationGroupId, allowedStatuses)
				}
				await updateOptimizationGroupMutation.mutateAsync(
					getOptimizationGroupPayload(data)
				)
				queryClient.invalidateQueries(groupsQueryParams)
				toast.success(
					t('toast.entity.saved', { entity: t('entity.optimization_group') }),
					{ duration: 5000, id: 'optimization-group' }
				)
			} catch (error) {
				Sentry.captureException(error)
				toast.error(
					t('toast.entity.failed', { entity: t('entity.optimization_group') }),
					{ duration: 5000, id: 'optimization-group' }
				)
			}
		})
	)
	const handleSubmitAndUpdate = methods.handleSubmit(
		checkExport(async (data) => {
			try {
				// This action will trigger a status change to -> Pending
				await updateMainScenarioMutation.mutateAsync(getScenarioPayload(data))
				// Wait for the status change to Pending
				if (optimization.status !== OptimizationStatus.Pending) {
					await waitGroupStatus(optimizationGroupId, allowedStatuses)
				}
				//  This action will trigger a status change to -> Pending or keep as it is
				await updateOptimizationGroupMutation.mutateAsync(
					getOptimizationGroupPayload(data)
				)
				// Commit changes and change status to -> New
				await commit()
				toast.success(
					t('toast.entity.saved', { entity: t('entity.optimization_group') }),
					{ duration: 5000, id: 'optimization-group' }
				)
				/* onFinish?.() */
			} catch (error) {
				Sentry.captureException(error)
				toast.error(
					t('toast.entity.failed', { entity: t('entity.optimization_group') }),
					{ duration: 5000, id: 'optimization-group' }
				)
			}
		})
	)
	const handleReset = useCallback(() => {
		ampli.ogSettingsResetForm()
		methods.reset()
	}, [])

	return (
		<OptimizationGroupItemContext
			value={{
				skuRules,
				optimizationGroup: optimizationGroupQuery.data!,
				optimization,
				methods,
				isLoading,
				isPending,
				salesEntitiesTree,
				productGroups,
				mainScenario: mainScenarioQuery.data!,
				handleSubmit,
				handleSubmitAndUpdate,
				handleReset,
			}}
		>
			{children}
		</OptimizationGroupItemContext>
	)
}
const getValidationResolver =
	(
		schema: JSONSchemaType<OptimizationGroupFormData>,
		{ salesEntitiesTree }: { salesEntitiesTree: TreeNodeType[] }
	) =>
	async (values: OptimizationGroupFormData, ctx: any, options: any) => {
		const { level, selected } = getCompactedSelection(
			salesEntitiesTree,
			values.sales_entities
		)
		const errors: Record<string, { message: string }> = {}
		if (values.product_groups.length === 0) {
			errors['product_groups'] = { message: 'No product groups selected' }
		}
		if (selected.length === 0) {
			errors['sales_entities'] = { message: 'No sales points selected' }
		}
		if (level === 1 && selected.length > 1) {
			errors['sales_entities'] = {
				message: 'Multiple sales points with sales level 1',
			}
		}

		/**
		 * Use Zod for validation for smoke validation
		 */
		const { error, data } =
			await optimizationGroupFormSchema.safeParseAsync(values)

		if (error) {
			return {
				values: {} as Record<string, never>,
				errors: toNestErrors(
					parseZod4Issues(
						error.issues,
						!options.shouldUseNativeValidation && options.criteriaMode === 'all'
					),
					options
				),
			} satisfies ResolverResult<OptimizationGroupFormData>
		}

		const baseValidationResult = await ajvResolver(schema, {
			strict: false,
			formats: fullFormats,
		})(data, ctx, options)

		return merge(baseValidationResult, { errors })
	}

export const useOptimizationGroupItemContext = () =>
	use(OptimizationGroupItemContext)
